import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTheme } from '../ThemeContext';
import { Card, Modal, Carousel, Badge } from 'react-bootstrap';
import { AlignCenter, Star, StarFill, Wallet2 } from 'react-bootstrap-icons';
import Lottie from 'lottie-react';
import scrollDownAnimation from '../assets/downButton.json';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useWebSocket } from './WebSocketContext';
import { FixedSizeGrid as Grid } from 'react-window';
import Codee from './ui/Codee';
import { useSalesFeedScrolling } from './ui/useSalesFeedScrolling';
import { useFilter } from './FilterContext';



const getColumnCountFor = (w) => {
  const width = Math.min(1504, w);
  if (width >= 1400) return [6, width / 6 - 8];
  if (width >= 1200) return [5, width / 5 - 8];
  if (width >= 992) return [4, width / 4 - 8];
  if (width >= 768) return [3, width / 3 - 8];
  if (width >= 576) return [3, width / 3 - 8];
  return [2, width / 2 - 8];
};

const getModalColumnCountFor = (w) => {
  const width = Math.max(280, w || 0);
  if (width >= 620) return [3, width / 3 - 10];
  return [2, width / 2 - 15];
};

const DEFAULT_BOTTOM_NAV_HEIGHT = 56;

const getViewportHeight = () => {
  if (typeof window === 'undefined') return 800;
  return Math.round(
    window.visualViewport?.height
    || window.innerHeight
    || document.documentElement?.clientHeight
    || 800
  );
};

const getBottomNavHeight = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return DEFAULT_BOTTOM_NAV_HEIGHT;
  }
  const nav = document.querySelector('.app-bottom-nav');
  if (!nav) return DEFAULT_BOTTOM_NAV_HEIGHT;
  const rect = nav.getBoundingClientRect();
  const height = Math.round(rect.height);
  const bottomGap = Math.max(0, Math.round(window.innerHeight - rect.bottom));
  const total = height + bottomGap;
  return total > 0 ? total : DEFAULT_BOTTOM_NAV_HEIGHT;
};

const SalesFeed = ({
  sales = [],
  type,
  targetArticle,
  containerWidth,
  modalGridHeight = 0,
  modalGridWidth = 0,
  modalTopInset = 0,
}) => {
  const { showFeedbackOnly } = useFilter();
  const filteredSales = showFeedbackOnly
    ? sales.filter(sale => sale.feedbackpoints)
    : sales;
  const { theme } = useTheme();
  const themeClass = theme === 'dark' ? 'text-white bg-dark' : 'text-dark bg-light';

  const { fetchMoreSales, fetchMoreFeedbackSales } = type === 'ws'
    ? useWebSocket()
    : { fetchMoreSales: null, fetchMoreFeedbackSales: null };


  const [modalVisible, setModalVisible] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [activeModalSlide, setActiveModalSlide] = useState(0);


  const initialW = (type === 'main' && containerWidth) ? containerWidth : window.innerWidth;
  const [columnCount, setColumnCount] = useState(
    (type === 'ws' || type === 'yesterday' || type === 'main')
      ? getColumnCountFor(initialW)
      : type === 'modal'
        ? getModalColumnCountFor(modalGridWidth || Math.min(window.innerWidth - 48, 620))
        : [2, Math.min(500, initialW) / 2 - 30]
  );
  const [viewportHeight, setViewportHeight] = useState(getViewportHeight);
  const [bottomNavHeight, setBottomNavHeight] = useState(DEFAULT_BOTTOM_NAV_HEIGHT);

  const updateViewportMetrics = useCallback(() => {
    setViewportHeight(getViewportHeight());
    if (type === 'ws' || type === 'yesterday') {
      setBottomNavHeight(getBottomNavHeight());
    }
  }, [type]);


  const handleResize = useCallback(() => {
    const w = type === 'modal'
      ? (modalGridWidth || Math.min(window.innerWidth - 48, 620))
      : (type === 'main' && containerWidth) ? containerWidth : window.innerWidth;
    setColumnCount(
      (type === 'ws' || type === 'yesterday' || type === 'main')
        ? getColumnCountFor(w)
        : type === 'modal'
          ? getModalColumnCountFor(w)
          : [2, Math.min(500, w) / 2 - 30]
    );
    updateViewportMetrics();
  }, [type, containerWidth, modalGridWidth, updateViewportMetrics]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  useEffect(() => {
    const viewport = window.visualViewport;
    const navElement = document.querySelector('.app-bottom-nav');
    let observer = null;

    if (viewport) {
      viewport.addEventListener('resize', updateViewportMetrics);
      viewport.addEventListener('scroll', updateViewportMetrics);
    }

    if (typeof ResizeObserver !== 'undefined' && navElement) {
      observer = new ResizeObserver(updateViewportMetrics);
      observer.observe(navElement);
    }

    updateViewportMetrics();

    return () => {
      if (viewport) {
        viewport.removeEventListener('resize', updateViewportMetrics);
        viewport.removeEventListener('scroll', updateViewportMetrics);
      }
      observer?.disconnect();
    };
  }, [updateViewportMetrics]);

  useEffect(() => {
    if (type === 'main') handleResize();
  }, [containerWidth, type, handleResize]);

  const {
    listRef,
    unseenCount,
    handleScroll,
    scrollToNewestMessage,
    isAutoScrolling
  } = useSalesFeedScrolling({
    sales: filteredSales,
    fetchMoreSales: type === 'ws'
      ? (showFeedbackOnly ? fetchMoreFeedbackSales : fetchMoreSales)
      : null,
    type,
    targetArticle,
    columnCount,
    rowHeight: 430,
    bottomInsetPx: type === 'ws' ? Math.max(0, bottomNavHeight + 12) : 0,
  });


  const openModal = (images) => {
    setModalImages(images);
    setActiveModalSlide(0);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setModalImages([]);
    setActiveModalSlide(0);
  };


  const renderImagesSlider = () => (
    <Modal
      show={modalVisible}
      onHide={closeModal}
      centered
      className={`sales-gallery-modal ${theme === 'dark' ? 'sales-gallery-modal--dark' : 'sales-gallery-modal--light'}`}
      dialogClassName="sales-gallery-dialog"
      backdropClassName="sales-gallery-backdrop"
      contentClassName={`sales-gallery-content ${theme === 'dark' ? 'sales-gallery-content--dark' : 'sales-gallery-content--light'}`}
    >
      <Modal.Body className="sales-gallery-body">
        <button
          type="button"
          className="sales-gallery-close"
          onClick={closeModal}
          aria-label="Закрыть галерею"
        >
          ×
        </button>

        <div className="sales-gallery-counter">
          {modalImages.length > 0 ? `${activeModalSlide + 1} / ${modalImages.length}` : ''}
        </div>

        <Carousel
          activeIndex={activeModalSlide}
          onSelect={(selectedIndex) => setActiveModalSlide(selectedIndex)}
          interval={null}
          controls={modalImages.length > 1}
          indicators={modalImages.length > 1}
          pause="hover"
          wrap={modalImages.length > 1}
          touch
          className="sales-gallery-carousel"
        >
          {modalImages.map((image, index) => (
            <Carousel.Item key={index}>
              <div className="sales-gallery-image-wrap">
                <img
                  className="sales-gallery-image"
                  src={image.replace('c246x328', 'c516x688')}
                  alt={`Фото товара ${index + 1}`}
                  loading="lazy"
                />
              </div>
            </Carousel.Item>
          ))}
        </Carousel>
      </Modal.Body>
    </Modal>
  );


  const decodeHTMLEntities = (text) => {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    return textArea.value;
  };


  const RatingStars = ({ rating, feedbacks }) => {
    const stars = [1, 2, 3, 4, 5].map((star) =>
      rating < star
        ? <Star key={star} size={14} color="orange" style={{ marginTop: 1 }} />
        : <StarFill key={star} size={14} color="orange" style={{ marginTop: 1 }} />
    );

    return (
      <div className={`rating-pill ${theme === 'dark' ? 'rating-pill--dark' : 'rating-pill--light'}`}>
        <div className="rating-pill__stars">
          {stars}
        </div>
        <span className="rating-pill__count"> | {feedbacks}</span>
      </div>
    );
  };


  const Time = ({ ts }) => {
    const date = new Date(ts * 1000);
    const hours = date.getHours();
    const minutes = ("0" + date.getMinutes()).slice(-2);
    const seconds = ("0" + date.getSeconds()).slice(-2);
    const formattedTime = `${hours}:${minutes}`;

    return <div className="time-style">{formattedTime}</div>;
  };

  const UnrefusableIcon = ({ theme }) => (
    <div style={{
      position: 'absolute',
      right: '2px',
      zIndex: 2,
      pointerEvents: 'none',
      animation: 'pulse 2s infinite'
    }}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="30"
        height="30"
        style={{
          filter: 'drop-shadow(0 0 8px rgba(255, 68, 68, 0.5))',
          position: 'relative',
          fill: 'red'
        }}
        viewBox="0 0 19 18"
      >
        <path fill="inherit" fillRule="evenodd" d="M10.6 5.1a.9.9 0 0 0-.9.9v3.2a.9.9 0 1 0 1.8 0V6a.9.9 0 0 0-.9-.9Zm0 7.8a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8Z" clipRule="evenodd" />
        <path fill="inherit" fillRule="evenodd" d="M10.072 2.18a6.703 6.703 0 0 0-3.653 1.455.974.974 0 0 1-.134.09c-.341.191-.774.163-1.057-.12-.322-.323-.327-.851.021-1.144a8.378 8.378 0 0 1 .342-.273A8.326 8.326 0 0 1 9.941.527a8.312 8.312 0 0 1 5.62 1.61 8.508 8.508 0 0 1 3.22 4.935 8.6 8.6 0 0 1-.737 5.869 8.43 8.43 0 0 1-4.339 3.96 8.289 8.289 0 0 1-5.838.132 8.415 8.415 0 0 1-4.51-3.758A8.584 8.584 0 0 1 2.27 8.067l-.856.744a.831.831 0 0 1-1.186-.037.858.858 0 0 1 .036-1.201l2.453-2.338a.833.833 0 0 1 1.223.079c.05.063.092.116.132.17.081.104.162.209.322.389l1.718 2.114a.858.858 0 0 1-.115 1.197.832.832 0 0 1-1.18-.116L3.931 7.98a6.917 6.917 0 0 0 .84 4.46 6.774 6.774 0 0 0 3.63 3.026 6.672 6.672 0 0 0 4.7-.107 6.785 6.785 0 0 0 3.492-3.187c.75-1.452.96-3.128.593-4.724a6.848 6.848 0 0 0-2.593-3.972 6.69 6.69 0 0 0-4.522-1.296Z" clipRule="evenodd" />
      </svg>
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '140%',
        height: '140%',
        borderRadius: '50%',
        pointerEvents: 'none'
      }} />
    </div>
  );

  const Vigoda = ({ price, price2, feedbackpoints, feedbackpoints2 }) => {

    if (!feedbackpoints2) return null;

    const calculateBenefit = (fp, p) => Math.round((fp / p) * 100);
    const currentBenefit = feedbackpoints2 - price2;
    const currentPercentage = calculateBenefit(feedbackpoints2, price2);


    let trend = 'same';
    if (feedbackpoints2 > feedbackpoints) trend = 'increased';
    else if (feedbackpoints2 < feedbackpoints) trend = 'decreased';


    const previousBenefit = feedbackpoints - price;
    const previousPercentage = calculateBenefit(feedbackpoints, price);

    return (
      <div className={`vigoda vigoda--${trend}`}>
        {feedbackpoints2 !== feedbackpoints && (
          <span className="vigoda__previous">
            {previousBenefit} ({previousPercentage}%)
          </span>
        )}
        {currentBenefit} ({currentPercentage}%)
      </div>
    );
  };


  const AIValue = ({ ai, price, price2 }) => {


    const aiInt = parseInt(ai, 10);
    const bgColor =
      price2 > price ? 'rgba(242, 73, 66, 0.8)' :
        price2 < price ? 'rgba(52, 152, 219, 0.8)' :
          'rgba(101, 211, 73, 0.8)';

    return (
      <div className="ai-style" style={{ backgroundColor: bgColor }}>
        AI: {aiInt}%
      </div>
    );
  };

  function Diapaz(diapazmin, diapazmax) {
    let mind = diapazmin == null ? 0 : diapazmin.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', maximumSignificantDigits: 21 });
    let maxd = diapazmax == null ? 0 : diapazmax.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', maximumSignificantDigits: 21 });
    let diapaz = `${mind} - ${maxd}`;
    if (mind === 0 && maxd === 0) {
      diapaz = null;
    }
    return diapaz;
  }

  const MarketType = ({ marketplace }) => {
    let marketColor;
    if (marketplace === 'wb') marketColor = 'rgba(138, 43, 226, 0.52)';
    else if (marketplace === 'ozon') marketColor = 'rgba(0, 127, 255, 0.52)';
    else marketColor = 'rgba(0, 194, 252, 0.52)';

    return (
      <div
        className={`market-type-pill ${theme === 'dark' ? 'market-type-pill--dark' : 'market-type-pill--light'}`}
        style={{ '--market-color': marketColor }}
      >
        <span className="market-type-pill__label">
          {marketplace.toUpperCase()}
        </span>
      </div>
    );
  };

  const FeedbackpointsMod = ({ feedbackpoints, feedbackpoints2 }) => {
    if (!feedbackpoints) return null;

    let status = 'same';
    if (!feedbackpoints2) status = 'removed';
    else if (feedbackpoints2 > feedbackpoints) status = 'increased';
    else if (feedbackpoints2 < feedbackpoints) status = 'decreased';

    return (
      <div className={`feedbackpoints-mod feedbackpoints-mod--${status}`}>
        <Wallet2 size={14} color="white" className="feedbackpoints-mod__icon" />

        {status === 'removed' && (
          <span className="feedbackpoints-mod__removed">
            {feedbackpoints}₽
          </span>
        )}

        {feedbackpoints2 !== feedbackpoints && feedbackpoints2 && (
          <>
            <span className="feedbackpoints-mod__previous">
              +{feedbackpoints}₽
            </span>
            <span className={`feedbackpoints-mod__current feedbackpoints-mod__current--${status}`}>
              +{feedbackpoints2}₽
            </span>
          </>
        )}

        {status === 'same' && (
          <span className="feedbackpoints-mod__current feedbackpoints-mod__current--same">
            +{feedbackpoints}₽
          </span>
        )}
      </div>
    );
  };

  const MarketTypeButton = ({ marketplace, article, seller }) => {
    const isWb = marketplace === 'wb';
    const marketHref = isWb
      ? `https://www.wildberries.ru/catalog/${article}/detail.aspx`
      : marketplace === 'ozon'
        ? `https://ozon.ru/products/${article}/`
        : `https://www.detmir.ru/product/index/id/${article}/`;

    const marketClass = isWb
      ? 'market-action-btn--wb'
      : marketplace === 'ozon'
        ? 'market-action-btn--ozon'
        : 'market-action-btn--detmir';

    if (isWb && seller) {
      return (
        <div className="market-action-row market-action-row--split">
          <a
            className={`market-action-btn ${marketClass}`}
            href={marketHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            Перейти
          </a>
          <a
            className="market-action-btn market-action-btn--seller"
            href={seller}
            target="_blank"
            rel="noopener noreferrer"
          >
            Перейти
          </a>
        </div>
      );
    }

    return (
      <div className="market-action-row">
        <a
          className={`market-action-btn market-action-btn--single ${marketClass}`}
          href={marketHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          Перейти
        </a>
      </div>
    );
  };
  const SkaldObr = ({ sklad, skladR }) => {
    const cleanString = (str) => {
      if (typeof str !== 'string') return 'un';
      return str
        .replace(/<b>/g, '')
        .replace(/<\/b>/g, '')
        .replace(/😍 /g, '')
        .replace(/⚠️ /g, '')
        .replace(/шт\./g, '')
        .replace(/WB - /g, '')
        .replace(/ПРОД - /g, '');
    };

    if (!skladR || skladR.trim() === '') {
      skladR = '0';
    }

    let chek_sklad = sklad ? sklad.split(' | ') : [];
    let chek_sklad2 = skladR.split(' | ');
    let formattedSklad1 = '';
    let formattedSklad2 = '';

    if (chek_sklad2.length > 1) {
      formattedSklad1 = `${cleanString(chek_sklad2[0])}/${cleanString(chek_sklad[0])}`;
      formattedSklad2 = `${cleanString(chek_sklad2[1])}/${cleanString(chek_sklad[1])}`;
    } else {
      formattedSklad1 = `${cleanString(chek_sklad2[0])}/${cleanString(chek_sklad[0])}`;
    }

    const getBadgeColor = (str) => {
      if (str && str.includes('WB - ')) return 'green';
      else if (str && str.includes('ПРОД - ')) return 'orange';
      return '';
    };

    return (
      <>
        {chek_sklad.length > 1 ? (
          <div className="stock-container">
            <p className={`stock-badge ${getBadgeColor(chek_sklad[0])}`}>
              {formattedSklad1}
            </p>
            <p className={`stock-badge ${getBadgeColor(chek_sklad[1])}`}>
              {formattedSklad2}
            </p>
          </div>
        ) : (
          <p className={`single-stock-badge ${getBadgeColor(chek_sklad[0])}`}>
            {formattedSklad1}
          </p>
        )}
      </>
    );
  };
  const getPhotos = (linkphoto) =>
    typeof linkphoto === 'string' && linkphoto.trim()
      ? linkphoto.split(',')
      : [];

  const parseSizes = (sizesString) => {
    if (!sizesString || typeof sizesString !== 'string') return [];
    return sizesString.split('|').map((item) => {
      const trimmed = item.trim();
      const isW = trimmed.startsWith('(W)');
      const isP = trimmed.startsWith('(P)');
      const color = isW ? 'green' : isP ? 'orange' : 'gray';
      const text = trimmed.replace('(W)', '').replace('(P)', '').trim();
      return { color, text };
    });
  };
  console.log()

  const Cell = React.memo(({ index, style, sale, themeClass, openModal, columnCount }) => {
    const sizesData = parseSizes(sale.sizes);
    const photos = getPhotos(sale.linkphoto);
    const fallback = '/no-photo.webp';
    const cellStyle = type === 'main'
      ? { ...style, boxSizing: 'border-box', padding: '10px 7px' }
      : { ...style, padding: '10px 0px 10px 15px' };
    return (
      <div
        key={sale.id}
        className="card-container"
        data-index={index}
        style={cellStyle}
      >
        <Card
          className={`mb-4 ${themeClass} sale-card${sale.feedbackpoints ? ' sale-card--feedback' : ''}`}
          style={{
            opacity: sale.col2 < 1 && sale.marketplace === 'wb' && type !== 'yesterday' && type !== 'main' ? '0.5' : '1',
          }}
        >
          <div
            className="image-container"
            onClick={() => photos.length && openModal(photos)}
          >
            <Card.Img
              variant="top"
              loading="lazy"
              src={photos[0] || fallback}
              className="sale-image"
              onError={(e) => { e.currentTarget.src = fallback; }}
            />
            <Time ts={sale.ts} />
            {sale.isunrefusable && <UnrefusableIcon />}
            {sale.feedbackpoints && <Vigoda price={sale.price} price2={sale.price2} feedbackpoints={sale.feedbackpoints} feedbackpoints2={sale.feedbackpoints2} />}
            {sale.marketplace === 'wb' && sale.ai >= 0 && (
              <AIValue ai={sale.ai} price={sale.price} price2={sale.price2} />
            )}
            {sale.sklad && <SkaldObr sklad={sale.sklad} skladR={sale.sklad2} />}

            {sale.col2 !== 0 && sizesData.length > 0 && (
              <div
                className="sizes-scroll"
                style={{
                  position: 'absolute',
                  top: 20,
                  right: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: '2px',
                  padding: '2px'
                }}
              >
                {sizesData.map((item, idx) => (
                  <span
                    key={idx}
                    className={`size-tag size-tag--${item.color}`}
                  >
                    {item.text}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="sale-content">
            <div className="price-container">
              <p
                className="price-text"
                style={{
                  backgroundColor:
                    sale.marketplace === 'wb'
                      ? sale.price2 > sale.price
                        ? '#f24942'
                        : sale.price2 < sale.price
                          ? '#3498db'
                          : '#65d349'
                      : '#65d349'
                }}
              >
                {sale.marketplace === 'wb' && sale.price2
                  ? sale.price2.toLocaleString('ru-RU', {
                    style: 'currency',
                    currency: 'RUB',
                    maximumSignificantDigits: 21
                  })
                  : sale.price.toLocaleString('ru-RU', {
                    style: 'currency',
                    currency: 'RUB',
                    maximumSignificantDigits: 21
                  })}
              </p>
              <div className="price-details">
                <p
                  className={`diapaz-text ${Diapaz(sale.diapazmin, sale.diapazmax) == null
                    ? 'no-diapaz'
                    : ''
                    }`}
                >
                  {Diapaz(sale.diapazmin, sale.diapazmax) == null
                    ? 'Нет диапазона'
                    : Diapaz(sale.diapazmin, sale.diapazmax)}
                </p>
                <>
                  <div className="price-row">
                    <p style={{ marginBottom: 0 }}>
                      {sale.marketplace === 'wb' && sale.price2 > sale.price
                        ? '📈'
                        : sale.marketplace === 'wb' && sale.price2 < sale.price
                          ? '📉'
                          : ''}
                    </p>
                    <s className="s-price">
                      {sale.marketplace === 'wb' &&
                        sale.price2 &&
                        sale.price !== sale.price2
                        ? sale.price.toLocaleString('ru-RU', {
                          style: 'currency',
                          currency: 'RUB',
                          maximumSignificantDigits: 21
                        })
                        : ''}
                    </s>
                  </div>
                </>
              </div>
            </div>

            <div className="sale-title-block">

              <p className="sale-name">
                {decodeHTMLEntities(sale.name)}
              </p>
              {sale.brand && (
                <div className="sale-brand" title={decodeHTMLEntities(sale.brand)}>
                  {decodeHTMLEntities(sale.brand)}
                </div>
              )}
            </div>
            <MarketType marketplace={sale.marketplace} />
            <FeedbackpointsMod feedbackpoints={sale.feedbackpoints} feedbackpoints2={sale.feedbackpoints2} />

            {sale.marketplace === 'wb' && (
              <RatingStars rating={sale.rating} feedbacks={sale.feedbacks} />
            )}
            {sale.subject && (
              <div className="subject-style">
                {sale.subject.split(/(<code>.*?<\/code>)/g).map((part, index) =>
                  part.includes('<code>') ? (
                    <Codee>
                      {part.replace(/<\/?code>/g, '')}
                    </Codee>
                  ) : (
                    <span key={index}>
                      {part.length > 40 ? part.slice(0, 40) + '...' : part}
                    </span>
                  )
                )}
              </div>
            )}
            <MarketTypeButton marketplace={sale.marketplace} article={sale.article} seller={sale.seller} />
          </div>
        </Card>
      </div>
    );
  });


  const cellRenderer = useCallback(
    ({ columnIndex, rowIndex, style, data }) => {
      const { sales, themeClass, openModal, columnCount } = data;
      const index = rowIndex * columnCount[0] + columnIndex;
      if (index >= sales.length) return null;

      const sale = sales[index];
      return (
        <Cell
          index={index}
          style={style}
          sale={sale}
          themeClass={themeClass}
          openModal={openModal}
          columnCount={columnCount}
        />
      );
    },
    []
  );

  const wsGridHeight = Math.max(240, viewportHeight);
  const yesterdayGridHeight = Math.max(240, viewportHeight);
  const mainGridHeight = 430;
  const resolvedModalGridHeight = Math.max(
    180,
    Math.floor(
      modalGridHeight > 0
        ? modalGridHeight
        : Math.max(220, viewportHeight - 260)
    )
  );
  const resolvedModalGridWidth = Math.max(
    280,
    Math.floor(
      modalGridWidth > 0
        ? modalGridWidth
        : Math.min(window.innerWidth - 48, 620)
    )
  );
  const defaultGridHeight = Math.max(240, viewportHeight - DEFAULT_BOTTOM_NAV_HEIGHT);
  const wsBottomInset = type === 'ws' ? Math.max(0, bottomNavHeight + 12) : 0;
  const modalTopInsetPx = type === 'modal' ? Math.max(0, Math.floor(Number(modalTopInset) || 0)) : 0;

  const gridInnerElementType = useMemo(() => {
    if (wsBottomInset <= 0) {
      return 'div';
    }
    const InnerElement = React.forwardRef(({ style, ...rest }, ref) => {
      const baseHeight = style?.height;
      let nextHeight = baseHeight;
      if (typeof baseHeight === 'number') {
        nextHeight = baseHeight + wsBottomInset;
      } else if (typeof baseHeight === 'string') {
        nextHeight = `calc(${baseHeight} + ${wsBottomInset}px)`;
      }
      return (
        <div
          ref={ref}
          style={{
            ...style,
            height: nextHeight,
          }}
          {...rest}
        />
      );
    });
    InnerElement.displayName = 'SalesFeedGridInner';
    return InnerElement;
  }, [wsBottomInset]);

  const modalGridInnerElementType = useMemo(() => {
    if (modalTopInsetPx <= 0) {
      return undefined;
    }
    const InnerElement = React.forwardRef(({ style, children, ...rest }, ref) => {
      const baseHeight = style?.height;
      let nextHeight = baseHeight;
      if (typeof baseHeight === 'number') {
        nextHeight = baseHeight + modalTopInsetPx;
      } else if (typeof baseHeight === 'string') {
        nextHeight = `calc(${baseHeight} + ${modalTopInsetPx}px)`;
      }
      return (
        <div
          ref={ref}
          style={{
            ...style,
            height: nextHeight,
            boxSizing: 'border-box',
          }}
          {...rest}
        >
          <div
            style={{
              transform: `translateY(${modalTopInsetPx}px)`,
              willChange: 'transform',
            }}
          >
            {children}
          </div>
        </div>
      );
    });
    InnerElement.displayName = 'SalesFeedModalGridInner';
    return InnerElement;
  }, [modalTopInsetPx]);

  const yesterdayGridInnerElementType = useMemo(() => {
    const InnerElement = React.forwardRef(({ style, children, ...rest }, ref) => {
      const baseHeight = style?.height;
      let nextHeight = baseHeight;
      if (typeof baseHeight === 'number') {
        nextHeight = `calc(${baseHeight}px + var(--y-content-top-inset, 0px) + var(--y-bottom-inset, 0px))`;
      } else if (typeof baseHeight === 'string') {
        nextHeight = `calc(${baseHeight} + var(--y-content-top-inset, 0px) + var(--y-bottom-inset, 0px))`;
      }
      return (
        <div
          ref={ref}
          style={{
            ...style,
            height: nextHeight,
            boxSizing: 'border-box',
          }}
          {...rest}
        >
          <div
            style={{
              transform: 'translateY(var(--y-content-top-inset, 0px))',
              willChange: 'transform',
              marginTop: '5px',
              marginLeft: '-7px',
            }}
          >
            {children}
          </div>
        </div>
      );
    });
    InnerElement.displayName = 'SalesFeedYesterdayGridInner';
    return InnerElement;
  }, []);

  const activeInnerElementType = type === 'ws'
    ? gridInnerElementType
    : type === 'yesterday'
      ? yesterdayGridInnerElementType
      : type === 'modal'
        ? modalGridInnerElementType
        : undefined;

  const typeStyle = () => {
    if (type === 'yesterday') {
      return {
        overflowY: 'auto',
        width: '100%',
        overflowX: 'hidden',
      };
    }
    if (type === 'modal') {
      return {
        overflowY: 'auto',
        overflowX: 'hidden',
      };
    }
    if (type === 'main') {
      return { overflowY: 'auto', height: mainGridHeight, width: '100%', overflowX: 'hidden' };
    }
    if (type === 'ws') {
      return {
        overflowY: 'auto',
        overflowX: 'hidden',
      };
    }
    return {
      overflowY: 'auto',
      height: 'auto',
      width: '80vh !important'
    };
  };



  return (
    <>
      {filteredSales.length > 0 ? (<Grid
        columnCount={
          type === 'yesterday' || type === 'ws' || type === 'main'
            ? columnCount[0]
            : filteredSales.length === 1
              ? 1
              : columnCount[0]
        }
        columnWidth={columnCount[1]}
        height={
          type === 'yesterday' ? yesterdayGridHeight
            : type === 'main' ? mainGridHeight
              : type === 'ws' ? wsGridHeight
                : type === 'modal' ? resolvedModalGridHeight
                  : defaultGridHeight
        }
        rowCount={Math.ceil(filteredSales.length / columnCount[0])}
        rowHeight={430}
        width={
          type === 'yesterday' ? window.innerWidth
            : type === 'ws' ? window.innerWidth
              : type === 'main' ? (containerWidth || Math.min(1504, window.innerWidth))
                : type === 'modal' ? resolvedModalGridWidth
                  : window.innerWidth * 0.8
        }
        itemData={{ sales: filteredSales, themeClass, openModal, columnCount }}
        children={cellRenderer}
        onScroll={type === 'ws' ? handleScroll : ''}
        ref={listRef}
        className={type === 'ws' ? 'scrollable sales-grid sales-grid--ws' : type === 'yesterday' ? 'scrollable sales-grid sales-grid--yesterday' : type === 'modal' ? 'scrollable sales-grid sales-grid--modal' : 'sales-grid'}
        innerElementType={activeInnerElementType}
        overscanRowCount={type === 'ws' ? 3 : 20}
        overscanColumnCount={type === 'ws' ? 2 : 20}
        style={typeStyle()}
      />) : (
        <div className="no-results">
          Ничего не найдено по выбранному фильтру. 🤔
        </div>
      )}

      {renderImagesSlider()}

      {!isAutoScrolling && type === 'ws' && (
        <div className="scroll-to-bottom-container">
          {unseenCount > 0 && (
            <Badge pill bg="primary" className="unseen-count-badge">
              {unseenCount}
            </Badge>
          )}

          <div className="scroll-to-bottom-button" onClick={scrollToNewestMessage}>
            <Lottie
              animationData={scrollDownAnimation}
              loop={false}
              initialSegment={[0, 70]}
              speed={2}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default SalesFeed;





